import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { Router } from "@angular/router";
import { HttpService } from '../services/http.service';
import { URLUtils } from '../urlUtils';


@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.css'],
    standalone: false
})
export class RegisterComponent implements OnInit, OnDestroy {
  submitted = false;
  
  /* ============================================
     PASSWORD REGISTRATION FORM - COMMENTED OUT
     ============================================
  myForm = this.formBuilder.group({
    fullname :['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    contact_person :['', [Validators.required]],
    contact_phone :['', [Validators.required]],
  })
  */

  // OTP Registration form - NOW THE PRIMARY REGISTRATION METHOD
  otpForm: any = FormGroup;
  otpSubmitted: boolean = false;
  otpSent: boolean = false;
  otpRequestInProgress: boolean = false;
  resendDisabled: boolean = false;
  resendTimer: number = 60;
  resendInterval: any;
  otpDigits: string[] = ['', '', '', '', '', ''];
  
  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private httpservice: HttpService) { }

  ngOnInit() {
    // OTP registration form
    this.otpForm = this.formBuilder.group({
      fullname: ['', [Validators.required]],
      identifier: ['', [Validators.required, this.emailOrMobileValidator]],
      contact_person: ['', [Validators.required]],
      country:[''],
      mobile:[''],
      category:[''],
      otp: ['', [Validators.pattern(/^[0-9]{6}$/)]]
    });
  }

  ngOnDestroy() {
    // Clean up interval on component destroy
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  }

  get otpF() { return this.otpForm.controls; }

  emailOrMobileValidator(control: any) {
    if (!control.value) return null;

    const value = control.value.trim();

    const emailRegex =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const mobileRegex =
      /^[6-9][0-9]{9}$/; // Indian mobile format

    if (emailRegex.test(value) || mobileRegex.test(value)) {
      return null;
    }

    return { invalidIdentifier: true };
  }

  // OTP Box Navigation Methods
  onOtpKeyUp(event: any, index: number, nextInput: any) {
    const input = event.target;
    const value = input.value;

    // Allow only numbers
    if (value && !/^\d$/.test(value)) {
      this.otpDigits[index - 1] = '';
      return;
    }

    // Move to next input if value entered
    if (value && nextInput) {
      nextInput.focus();
    }

    // Handle backspace
    if (event.key === 'Backspace' && !value && index > 1) {
      const previousInput = event.target.previousElementSibling;
      if (previousInput) {
        previousInput.focus();
      }
    }

    // Update the form control with combined OTP
    this.updateOtpFormControl();
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');
    
    this.otpDigits = [...digits, ...Array(6 - digits.length).fill('')];
    this.updateOtpFormControl();
  }

  updateOtpFormControl() {
    const otpValue = this.otpDigits.join('');
    this.otpForm.patchValue({ otp: otpValue });
  }

  // Cancel OTP process and reset form
  cancelRegistration() {
    this.otpSubmitted = false;
    this.otpSent = false;
    this.otpRequestInProgress = false;
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpForm.reset();
    this.stopResendTimer();
  }

  onSubmit() {
    this.otpSubmitted = true;
    
    // Clear previous errors
    const errorElements = ['error-fullname', 'error-identifier', 'error-contact_person'];
    errorElements.forEach(id => {
      const element = document.getElementById(id);
      if (element) element.textContent = '';
    });

    // Validate required fields before sending OTP
    if (this.otpForm.get('fullname').invalid || 
        this.otpForm.get('identifier').invalid || 
        this.otpForm.get('contact_person').invalid) {
      return;
    }

    if (!this.otpSent) {
      // Request OTP
      this.requestOTP();
    } else {
      // Verify OTP and complete registration
      if (this.otpForm.get('otp').invalid) {
        return;
      }
      this.verifyOTPAndRegister();
    }
  }

  // Request OTP from server
  requestOTP() {
    this.otpRequestInProgress = true;

    const identifier = this.otpForm.getRawValue()['identifier']?.trim();
    const isEmail = identifier.includes('@');

    const payload: any = {
      fullname: this.otpForm.value['fullname'],
      contact_person: this.otpForm.value['contact_person']
    };

    if (isEmail) {
      payload.email = identifier.toLowerCase();
    } else {
      payload.contact_phone = identifier;
    }

    // Using actual API call for OTP request
    this.httpservice.sendOtpPostRequest(URLUtils.registerRequestOTP, payload).subscribe(
      (resp: any) => {
        this.otpRequestInProgress = false;
        if (!resp['error']) {
          this.otpSent = true;
          this.otpForm.get('identifier').disable();
          this.otpForm.get('fullname').disable();
          this.otpForm.get('contact_person').disable();
          
          this.otpForm.get('otp').setValidators([
            Validators.required,
            Validators.pattern(/^[0-9]{6}$/)
          ]);
          this.otpForm.get('otp').updateValueAndValidity();
          
          this.startResendTimer();
          const alertElement = document.getElementById('id_alert');
          if (alertElement) {
            alertElement.className = 'alert alert-success';
            alertElement.textContent = resp['msg'] || 'OTP sent successfully';
          }
        } else {
          if (typeof resp['msg'] == 'object') {
            for (var key in resp['msg']) {
              const errorElement = document.getElementById('error-' + key);
              if (errorElement) errorElement.textContent = resp['msg'][key];
            }
          } else {
            const alertElement = document.getElementById('id_alert');
            if (alertElement) {
              alertElement.className = 'alert alert-danger';
              alertElement.textContent = resp['msg'];
            }
          }
        }
      },
      (error) => {
        this.otpRequestInProgress = false;
        const alertElement = document.getElementById('id_alert');
        if (alertElement) {
          alertElement.className = 'alert alert-danger';
          alertElement.textContent = 'Error sending OTP. Please try again.';
        }
      }
    );

    /* MOCK IMPLEMENTATION - COMMENTED OUT - Remove this section when URLUtils.registerRequestOTP is ready
    console.log('Requesting OTP for:', payload);
    setTimeout(() => {
      this.otpRequestInProgress = false;
      this.otpSent = true;
      this.otpForm.get('identifier').disable();
      this.otpForm.get('fullname').disable();
      this.otpForm.get('contact_person').disable();
      
      this.otpForm.get('otp').setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]{6}$/)
      ]);
      this.otpForm.get('otp').updateValueAndValidity();
      
      this.startResendTimer();
      const alertElement = document.getElementById('id_alert');
      if (alertElement) {
        alertElement.className = 'alert alert-success';
        alertElement.textContent = 'OTP sent successfully (Mock - use any 6 digits for testing)';
      }
    }, 1000);
    */
  }

  // Verify OTP and complete registration
  verifyOTPAndRegister() {
    this.otpRequestInProgress = true;

    const identifier = this.otpForm.get('identifier')?.value;
    const otp = this.otpForm.value['otp'];

    const payload: any = {
      fullname: this.otpForm.get('fullname')?.value,
      contact_person: this.otpForm.get('contact_person')?.value,
      otp: otp
    };

    const isEmail = identifier.includes('@');
    if (isEmail) {
      payload.email = identifier.toLowerCase();
    } else {
      payload.contact_phone = identifier;
    }

    // TODO: Replace with your actual registration verification API call
    this.httpservice.sendPostRequest(URLUtils.register, payload).subscribe(
      (resp: any) => {
        this.otpRequestInProgress = false;
        if (!resp['error']) {
          // Registration successful - store user data and redirect to dashboard
          if (resp['token']) {
            this.httpservice.setToken(resp['token']);
            localStorage.setItem('TOKEN', resp['token']);
          }
          
          if (resp['data']) {
            // Store user information
            if (resp['data']['id']) localStorage.setItem('user_id', resp['data']['id']);
            if (resp['data']['name']) localStorage.setItem('name', resp['data']['name']);
            if (resp['data']['role']) localStorage.setItem('role', resp['data']['role']);
            if (resp['data']['admin']) localStorage.setItem('isadmin', resp['data']['admin']);
            if (resp['data']['uid']) localStorage.setItem('jid', resp['data']['uid']);
            if (resp['data']['firm_name']) localStorage.setItem('firm_name', resp['data']['firm_name']);
            if (resp['data']['email']) localStorage.setItem('AAM_email', JSON.stringify(resp['data']['email']));
            if (resp['data']['plan']) localStorage.setItem('plan', resp['data']['plan'].toLowerCase());
            if (resp['data']['pk']) localStorage.setItem('prof_id', resp['data']['pk'].toLowerCase());
            if (resp['data']['profuid']) localStorage.setItem('profuid', resp['data']['profuid'].toLowerCase());
            if (resp['data']['groups']) localStorage.setItem('user_groups', JSON.stringify(resp['data']['groups']));
            if (resp['data']['subscription']) {
              localStorage.setItem('isPaidSub', JSON.stringify(resp['data']['subscription']['is_active']));
            }
          }

          // Success message
          const msg = resp['msg'] || "Registration successful! Redirecting to dashboard...";
          const alertElement = document.getElementById('id_alert');
          if (alertElement) {
            alertElement.className = 'alert alert-success';
            alertElement.textContent = msg;
          }

          // Redirect to dashboard after a short delay
          setTimeout(() => {
            this.router.navigate(['/grid']);
          }, 1500);
        } else {
          // Handle registration errors
          if (typeof resp['msg'] == 'object') {
            for (var key in resp['msg']) {
              const errorElement = document.getElementById('error-' + key);
              if (errorElement) errorElement.textContent = resp['msg'][key];
            }
          } else {
            const alertElement = document.getElementById('id_alert');
            if (alertElement) {
              alertElement.className = 'alert alert-danger';
              alertElement.textContent = resp['msg'];
            }
          }
        }
      },
      (error) => {
        this.otpRequestInProgress = false;
        const alertElement = document.getElementById('id_alert');
        if (alertElement) {
          alertElement.className = 'alert alert-danger';
          alertElement.textContent = 'Error verifying OTP. Please try again.';
        }
      }
    );

    /* MOCK IMPLEMENTATION - COMMENTED OUT - Remove this section when using real API
    console.log('Verifying OTP and registering:', payload);
    setTimeout(() => {
      this.otpRequestInProgress = false;
      const msg = "Registration successful! Redirecting to dashboard...";
      const alertElement = document.getElementById('id_alert');
      if (alertElement) {
        alertElement.className = 'alert alert-success';
        alertElement.textContent = msg;
      }
      
      // Simulate storing token and user data
      localStorage.setItem('TOKEN', 'mock_token_12345');
      localStorage.setItem('name', this.otpForm.get('contact_person')?.value);
      localStorage.setItem('firm_name', this.otpForm.get('fullname')?.value);
      
      // Redirect to dashboard
      setTimeout(() => {
        this.router.navigate(['/grid']);
      }, 1500);
    }, 1000);
    */
  }

  // Resend OTP
  resendOTP() {
    if (this.resendDisabled) {
      return;
    }

    this.otpSubmitted = false;
    this.otpForm.get('otp').reset();
    this.otpDigits = ['', '', '', '', '', ''];
    this.requestOTP();
  }

  // Start resend timer
  startResendTimer() {
    this.resendDisabled = true;
    this.resendTimer = 60;

    this.resendInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.stopResendTimer();
      }
    }, 1000);
  }

  // Stop resend timer
  stopResendTimer() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
    this.resendDisabled = false;
    this.resendTimer = 60;
  }

  /* ============================================
     PASSWORD REGISTRATION - COMMENTED OUT
     ============================================
  get f() { return this.myForm.controls; }

  onSubmit() {
    this.submitted = true
    // $("#id_alert").removeClass("alert alert-danger").text("")
    // $("#id_alert").removeClass("alert alert-success").text("")
    // if (this.myForm.invalid) { return; }
    // var payload = JSON.stringify(this.myForm.value)
    // this.ajax.post(URLUtils.register, payload).subscribe(
    //   (resp) => {
    //     if (!resp['error']) {
    //       var msg = "Thank you very much for your request. One of our representatives will contact you soon."
    //       $("#id_alert").addClass("alert alert-success").text(msg)
    //       this.ajax.success_alert(msg)
    //       this.myForm.reset()
    //     }
    //     else {
    //       if(typeof resp['msg'] == 'object')
    //       {
    //         for (var key in resp['msg']) {
    //           $('#error-' + key).text(resp['msg'][key])
    //         }
    //       }
    //       else
    //         $("#id_alert").addClass("alert alert-danger").text(resp['msg'])
    //     }
    //   }
    // )
  }
  */

}